import type { ReactNode } from "react";
import { flexRender, type Row, type Table } from "@tanstack/react-table";

import { DataTable, type DataTableDensity } from "@/components/ds";

// A TanStack table drawn by ds DataTable (ZTA cascade plan 4.3). ZTA-only.
//
// ZTA's list pages build their tables with @tanstack/react-table: typed column
// helpers, cell renderers that read row.original. Every one uses the core row
// model only (no TanStack sorting, filtering, paging or expansion), so each
// visible leaf column maps one-to-one onto a ds column, and headers and cells
// are rendered by TanStack's own flexRender, exactly as the old local
// components/DataTable.tsx rendered them. What changes is the table itself:
// ds DataTable's markup, density, hover, skeleton and empty state.
//
// New tables should use ds DataTable directly, with ds columns.
//
// Design evaluation F36: every table needs a name, so `aria-label` is
// required, and a column opts into ds DataTable's sorting through its
// TanStack meta: `meta: { sortable: true }` sorts by the column's accessor
// value, or `meta: { sortable: true, sortValue: (row) => … }` by a value of
// its own (a date, a rank). ds DataTable sorts the loaded rows.

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    sortable?: boolean;
    sortValue?: (row: TData) => string | number | boolean | Date | null | undefined;
  }
}

export interface TanstackTableProps<TData> {
  table: Table<TData>;
  /** Required: every table needs an accessible name (design eval F36). */
  "aria-label": string;
  /** Draws ds DataTable's skeleton rows. */
  loading?: boolean;
  /** Shown in the table body when there are no rows. Default: nothing, as before. */
  empty?: ReactNode;
  density?: DataTableDensity;
  onRowClick?: (row: TData) => void;
  className?: string;
}

export function TanstackTable<TData>({ table, loading, empty = null, density, onRowClick, className, ...rest }: TanstackTableProps<TData>) {
  const headers = table.getFlatHeaders().filter((h) => h.subHeaders.length === 0);
  const columns = headers.map((header) => {
    const meta = header.column.columnDef.meta;
    return {
      key: header.column.id,
      header: header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext()),
      cell: (row: Row<TData>) => {
        const cell = row.getVisibleCells().find((c) => c.column.id === header.column.id);
        return cell ? flexRender(cell.column.columnDef.cell, cell.getContext()) : null;
      },
      ...(meta?.sortable
        ? {
            sortable: true,
            sortValue: (row: Row<TData>) =>
              meta.sortValue ? meta.sortValue(row.original) : (row.getValue(header.column.id) as string | number | null | undefined),
          }
        : {}),
    };
  });
  const rows = table.getRowModel().rows;
  return (
    <DataTable<Row<TData>>
      aria-label={rest["aria-label"]}
      className={className}
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      loading={loading}
      empty={empty}
      density={density}
      onRowClick={onRowClick ? (row) => onRowClick(row.original) : undefined}
    />
  );
}
